/**
 * "Not you?" at a live's door, and the first visit to a quick live: a first name and an email, the bot check where
 * the address asks for one, and in. The seat is the register page's own (`POST /w/:slug/register`): the same email
 * gets the same seat back, and the page it hands back is the person's room.
 */
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { ATTRIBUTION_KEYS, ATTRIBUTION_MAX } from "../../../shared/measure";
import type { PublicRegisterResponse } from "../../../shared/types";
import { ApiError, api } from "../../lib/api";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id: string) => void;
      remove: (id: string) => void;
    };
  }
}

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  return new Promise((resolve, reject) => {
    let s = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`);
    if (!s) {
      s = document.createElement("script");
      s.src = TURNSTILE_SRC;
      s.async = true;
      document.head.appendChild(s);
    }
    s.addEventListener("load", () => resolve(), { once: true });
    s.addEventListener("error", () => reject(new Error("bot check did not load")), { once: true });
  });
}

function cookie(name: string): string {
  const m = document.cookie.match(new RegExp(`(^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[2]) : "";
}

/**
 * Where this person came from, as the register page's own runtime reports it:
 * the link's tags (or the first-touch ones this browser kept), the sending
 * site, and how long the page was open before they joined.
 */
export function doorAttribution(openedAt: number, nowMs = Date.now()): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const q = new URLSearchParams(window.location.search);
  for (const k of ATTRIBUTION_KEYS) {
    const v = q.get(k);
    if (v) out[k] = v.slice(0, ATTRIBUTION_MAX);
  }
  if (Object.keys(out).length > 0) {
    out.landing = (window.location.pathname + window.location.search).slice(0, 500);
    out.first_seen = Math.round(nowMs / 1000);
  } else {
    try {
      const kept = cookie("fr_attr");
      if (kept) Object.assign(out, JSON.parse(kept) as Record<string, unknown>);
    } catch {
      /* a kept cookie we cannot read says nothing */
    }
  }
  try {
    const ref = document.referrer;
    if (ref && new URL(ref).hostname !== window.location.hostname) out.ref = ref.slice(0, 500);
  } catch {
    /* no sending site */
  }
  out.seconds = Math.round((nowMs - openedAt) / 1000);
  return out;
}

export function JoinForm({
  slug,
  siteKey,
  button,
  className,
  onIn,
  note,
}: {
  slug: string;
  siteKey: string | null;
  /** "Join now", or "Watch the replay" once the live is over. */
  button: string;
  className?: string;
  onIn: (res: PublicRegisterResponse) => void;
  /** The line over the fields; the host will know they came. */
  note: string;
}): ReactNode {
  const [first, setFirst] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [checkToken, setCheckToken] = useState("");
  const openedAt = useRef(Date.now());
  const checkBox = useRef<HTMLDivElement>(null);
  const checkId = useRef<string | null>(null);
  const firstField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstField.current?.focus();
  }, []);
  useEffect(() => {
    if (!siteKey) return;
    let alive = true;
    loadTurnstile()
      .then(() => {
        if (!alive || !checkBox.current || !window.turnstile) return;
        checkId.current = window.turnstile.render(checkBox.current, {
          sitekey: siteKey,
          callback: (t: string) => setCheckToken(t),
          "expired-callback": () => setCheckToken(""),
          "error-callback": () => setCheckToken(""),
        });
      })
      .catch(() => {
        /* the server turns the join away and says to try again */
      });
    return () => {
      alive = false;
      if (checkId.current) window.turnstile?.remove(checkId.current);
      checkId.current = null;
    };
  }, [siteKey]);

  const join = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!first.trim() || !email.trim()) {
      setError(!first.trim() ? "Add your first name." : "Add the email you signed up with.");
      return;
    }
    setBusy(true);
    setError("");
    api.public
      .register(slug, {
        first_name: first.trim(),
        email: email.trim(),
        ...(checkToken ? { turnstile_token: checkToken } : {}),
        attribution: doorAttribution(openedAt.current),
      })
      .then(onIn, (err: unknown) => {
        setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
        if (checkId.current) window.turnstile?.reset(checkId.current);
        setCheckToken("");
        setBusy(false);
      });
  };

  return (
    <form className={className} noValidate onSubmit={join}>
      <p className="muted">{note}</p>
      <div className="fl">
        <input dir="auto" ref={firstField} id="jf-first" name="first" placeholder=" " autoComplete="given-name" value={first} onChange={(e) => setFirst(e.target.value)} />
        <label htmlFor="jf-first">First name</label>
      </div>
      <div className="fl">
        <input id="jf-email" name="email" type="email" placeholder=" " autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <label htmlFor="jf-email">Email</label>
      </div>
      {siteKey ? <div ref={checkBox} /> : null}
      <button className="btn btn-p" type="submit" disabled={busy}>
        {busy ? <span className="spin" /> : null}
        {button}
      </button>
      <p className="f-err" role="alert">
        {error}
      </p>
    </form>
  );
}
